/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import {
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  ibanFieldDefault,
  postalCodeDefault,
  textFieldDefault,
} from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import addressFields from "../modules/addressFields";
import InputAdornment from "@mui/material/InputAdornment";
import React from "react";
import _ from "lodash";
import { UseForm } from "../../../../types/UseForm";
import {
  addProcessParticipant,
  aiConcernDescription,
  caseInsurance,
  extraJudicialProcedure,
  getAdditionalClientsForm,
  getNextInstanceSection,
  getUnderageClientForm,
  judicialProcedure,
  litigationCourt,
  opponentLawyer,
  opposingLiabilityInsurance,
  processParticipantsAuthority,
  processParticipantsOrganizations,
  processParticipantsPeople,
  prosecution,
} from "../abstractLitigationCase/fullFormDefinition";
import { BackofficeUser } from "../../../../types/BackofficeUser";
import { IbanInput } from "../../../../components/MaskedInputs/IbanInput";
import legalForms from "../../../legalForms";
import { getGeneralCivilLawCaseRejectionCategorySelectOptions } from "./generalCivilLawFormHelperService";
import { translate } from "../../../Translations/translatorService";
import { GeneralCivilLaw } from "../../../../types/Products/GeneralCivilLaw";

import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";

export const fullFormDefinition = (product: GeneralCivilLaw, currentUser: BackofficeUser) => {
  const incidentBaseCreated = {
    isHidden: ({ product }: { product: GeneralCivilLaw }) => product.stage! > stagesCase.STAGE_CREATED,
    label: "Allgemeines zum Fall",
    elements: [...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true })],
  };

  const generalCivilLawBase = {
    isHidden: ({ product }: { product: GeneralCivilLaw }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Allgemeines zum Fall",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.affectedPartyType`,
        isDisabled: () => true,
        options: () => [{ value: "customer" }, { value: "coinsured" }, { value: "unknown" }],
      },
      ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
      ...addressFields({
        path: `${product.productClassName}.client.residenceAddress`,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.advisoryHandling,
      }),
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.client.birthDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.objectivesClient`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.caseCategory !== "enforceClaim",
        additionalProps: {
          multiple: true,
          fullWidth: true,
        },
        options: () => [
          { value: "payment" },
          { value: "injunction" },
          { value: "removal" },
          { value: "handover" },
          { value: "other" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientHasAlreadyClaimed`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.caseCategory !== "enforceClaim",
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientHasAlreadyClaimed`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDateClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.caseCategory !== "enforceClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.claimedDeadlineClient`,
        validators: dateFieldDefault,
        isHidden: ({ product }: { product: GeneralCivilLaw }) =>
          product.caseCategory !== "enforceClaim" || !product.clientHasAlreadyClaimed,
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountClient`,
        isHidden: ({ product }: { product: GeneralCivilLaw }) =>
          product.caseCategory !== "enforceClaim" || product.objectivesClient.indexOf("payment") === -1,
        numberType: "float",
        fieldType: "money",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.client.bankData.iban`,
        validators: ibanFieldDefault,
        additionalProps: {
          InputProps: {
            inputComponent: IbanInput,
            startAdornment: <InputAdornment position="start">DE</InputAdornment>,
          },
        },
      },
    ],
  };

  const incidentDetails = {
    isHidden: ({ product }: { product: GeneralCivilLaw }) =>
      product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Details zum Fall",
    elements: [
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.incidentDate`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.incidentFrequency`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: () => [{ value: "once" }, { value: "multiple" }],
      },
      ...addressFields({
        path: `${product.productClassName}.incidentAddress`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.incidentLocalityDescription !== null,
      }),
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.policeInvolved`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.policeInvolved`),
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.policeReportReferenceNumber`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ values }: { values: GeneralCivilLaw }) => values.generalCivilLaw.policeInvolved !== true,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.policeDepartmentName`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ values }: { values: GeneralCivilLaw }) => values.generalCivilLaw.policeInvolved !== true,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.incidentLocalityDescription`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product }: { product: GeneralCivilLaw }) => product.incidentLocalityDescription === null,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };

  const opposingParty = {
    isHidden: ({ product }: { product: GeneralCivilLaw }) =>
      product.advisoryHandling || product.stage < stagesCase.STAGE_DATA_COMPLETED,
    label: "Gegenseite",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalName`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: GeneralCivilLaw }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.legalForm`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: GeneralCivilLaw }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        options: () => [
          { value: "__null__", label: "Keine Gesellschaftsform" },
          ..._.map(legalForms, (legalForm) => {
            return {
              value: legalForm.label,
              label: legalForm.label,
            };
          }),
        ],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.streetAddress`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postalCode`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.addressLocality`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxNumber`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxPostalCode`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: [...textFieldDefault, postalCodeDefault],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.address.postOfficeBoxAddressLocality`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.mainEmail.email`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.telephone`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: textFieldDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.fax`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyLegalEntity",
      },
      {
        type: "ContractRepresentatives",
        path: `${product.productClassName}.opposingPartyLegalEntity.organization.additionalRepresentatives`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.opposingPartyType === "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
          gridSpacing: 4,
          gridSize: 4,
          buttonFullWidth: false,
        },
      },
      ...personFields({
        path: `${product.productClassName}.opposingPartyPerson`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: GeneralCivilLaw; values: UseForm["values"] }) =>
          product.stage >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyPerson.telephone`,
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.opposingPartyPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values.generalCivilLaw.opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage >= stagesCase.STAGE_COMPLETED,
      }),
    ],
  };

  return {
    product: {
      sections: [
        incidentBaseCreated,
        generalCivilLawBase,
        incidentDetails,
        aiConcernDescription(product),
        getAdditionalClientsForm(product),
        getUnderageClientForm(product, "Minderjährige Mandantschaft"),
      ],
    },
    additional: {
      sections: [
        {
          label: "Allgemeines",
          elements: [
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.reference`,
              isDisabled: () => true,
            },
            {
              type: "ValidatorTextField",
              path: `${product.productClassName}.powerOfAttorney`,
              isHidden: ({ product }: { product: GeneralCivilLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: () => true,
            },
            {
              type: "ValidatorDateField",
              path: `${product.productClassName}.timeBarredDate`,
              validators: dateFieldDefault,
              isHidden: ({ product }: { product: GeneralCivilLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling,
              isDisabled: ({ product }: { product: GeneralCivilLaw }) => product.stage! >= stagesCase.STAGE_CHECKED,
            },
            {
              type: "ValidatorSelect",
              path: `${product.productClassName}.caseCategoryAssessment`,
              isHidden: ({ product }: { product: GeneralCivilLaw }) =>
                product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
              isDisabled: ({ product }: { product: GeneralCivilLaw }) =>
                product.stage! > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
              options: () => [{ value: "confirm" }, { value: "reject" }],
            },
          ],
        },
        extraJudicialProcedure(product, currentUser, [], getGeneralCivilLawCaseRejectionCategorySelectOptions),
        judicialProcedure(product),
        {
          label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
          isHidden: ({ product }: { product: GeneralCivilLaw }) => product.courtAppointments.length === 0,
          elements: getCourtAppointmentFields(product),
        },
        getNextInstanceSection(product),
      ],
    },
    processParticipants: {
      asAccordion: true,
      sections: [
        opposingParty,
        opponentLawyer(product),
        caseInsurance(product),
        {
          ...processParticipantsAuthority(product),
          isHidden: ({ product }: { product: GeneralCivilLaw }) => product.advisoryHandling,
        },
        prosecution(product),
        {
          ...opposingLiabilityInsurance(product),
          isHidden: ({ product }: { product: GeneralCivilLaw }) => product.advisoryHandling,
        },
      ],
    },
    additionalProcessParticipants: {
      isHidden: () => product.advisoryHandling,
      sections: [
        addProcessParticipant(),
        processParticipantsPeople(product),
        processParticipantsOrganizations(product),
      ],
      withoutSubmit: true,
    },
    court: {
      isHidden: () => product.advisoryHandling,
      sections: [
        litigationCourt(product, [
          "Amtsgericht",
          "Arbeitsgericht",
          "Landgericht",
          "Mahngericht",
          "Oberlandesgericht",
          "Familiengericht",
        ]),
      ],
    },
  };
};
